import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useCoreAuth } from './CoreAuthContext';
import { UnifiedCreditService } from '../lib/unified-credit-service';
import { subscribeToCreditChanges } from '../lib/credit-events';
import { ProfileService, type CompleteUserProfile } from '../lib/profile-service';
import type { User } from '../lib/database.types';

interface AuthUser extends CompleteUserProfile {
  role: 'admin' | 'member';
  system_role?: string | null;
}

interface UserContextType {
  user: AuthUser | null;
  isLoadingProfile: boolean;
  hasInitialLoad: boolean;
  refreshUserProfile: () => Promise<void>;
  updateUserProfile: (updates: Partial<User>) => Promise<void>;
  hasTutorialCompleted: () => boolean;
  markTutorialCompleted: () => Promise<void>;
  hasOnboardingCompleted: () => boolean;
  markOnboardingCompleted: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: ReactNode }) {
  const { supabaseUser, session, isInitialized } = useCoreAuth();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  
  // Refs for preventing concurrent fetches
  const fetchingRef = useRef(false);
  const lastFetchRef = useRef(0);
  const creditUnsubscribeRef = useRef<(() => void) | null>(null);

  const FETCH_COOLDOWN = 3000; // 3 seconds

  const fetchUserProfile = useCallback(async (userId: string, force = false) => {
    // Prevent concurrent fetches
    if (fetchingRef.current && !force) {
      return;
    }

    // Rate limiting (but allow force override for initial load)
    const now = Date.now();
    if (!force && now - lastFetchRef.current < FETCH_COOLDOWN) {
      return;
    }

    fetchingRef.current = true;
    lastFetchRef.current = now;
    setIsLoadingProfile(true);

    try {
      // Use ProfileService to get complete user profile
      const profile = await ProfileService.getUserProfile(userId);

      // Get credit balance
      let credits = 5; // Default
      try {
        const creditData = await UnifiedCreditService.getSimpleCreditInfo(userId);
        credits = creditData.credits;
      } catch (error) {
        console.error('Error fetching credits:', error);
      }

      if (profile) {
        // Use actual database data with profile information
        const fullUser: AuthUser = {
          ...profile,
          credits,
          role: 'admin', // Default role
          avatar: profile.avatar || supabaseUser?.user_metadata?.avatar_url || null,
          name: profile.name || supabaseUser?.user_metadata?.name || supabaseUser?.email?.split('@')[0] || 'User'
        };
        
        setUser(fullUser);
        setHasInitialLoad(true);
      } else {
        // Create new user if not found - use ProfileService for initialization
        try {
          const newUserData = await ProfileService.initializeProfile(userId, {
            email: supabaseUser?.email || '',
            name: supabaseUser?.user_metadata?.name || supabaseUser?.email?.split('@')[0] || 'User',
            avatar: supabaseUser?.user_metadata?.avatar_url || null
          });

          const newUser: AuthUser = {
            ...newUserData,
            credits,
            role: 'admin',
            // Initialize profile fields as undefined
            company: undefined,
            job_title: undefined,
            bio: undefined
          };
          
          setUser(newUser);
          setHasInitialLoad(true);
        } catch (initError) {
          console.error('Error initializing user profile:', initError);
          // Fallback to basic user object
          const fallbackUser: AuthUser = {
            id: userId,
            email: supabaseUser?.email || '',
            subscription_plan: null,
            credits: credits,
            tutorial_completed: false,
            organization_id: null,
            role: 'admin',
            created_at: supabaseUser?.created_at || new Date().toISOString(),
            updated_at: new Date().toISOString(),
            name: supabaseUser?.user_metadata?.name || supabaseUser?.email?.split('@')[0] || 'User',
            avatar: supabaseUser?.user_metadata?.avatar_url || null,
            team_role: null,
            company: undefined,
            job_title: undefined,
            bio: undefined
          };
          
          setUser(fallbackUser);
          setHasInitialLoad(true);
        }
      }

    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      fetchingRef.current = false;
      setIsLoadingProfile(false);
    }
  }, [supabaseUser]);

  // Set up credit change listener
  useEffect(() => {
    if (!user?.id) return;

    
    creditUnsubscribeRef.current = subscribeToCreditChanges(user.id, async (event) => {
      
      try {
        const creditData = await UnifiedCreditService.getSimpleCreditInfo(user.id, true);
        setUser(prev => prev ? { ...prev, credits: creditData.credits } : null);
      } catch (error) {
        console.error('Error updating credits:', error);
      }
    });

    return () => {
      if (creditUnsubscribeRef.current) {
        creditUnsubscribeRef.current();
        creditUnsubscribeRef.current = null;
      }
    };
  }, [user?.id]);

  // Initialize user when auth is ready
  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (supabaseUser && session) {
      // Set loading state immediately when we start fetching
      setIsLoadingProfile(true);
      fetchUserProfile(supabaseUser.id, true); // Force initial load
    } else {
      setUser(null);
      setIsLoadingProfile(false);
      if (creditUnsubscribeRef.current) {
        creditUnsubscribeRef.current();
        creditUnsubscribeRef.current = null;
      }
    }
  }, [supabaseUser?.id, session, isInitialized, fetchUserProfile]);

  const refreshUserProfile = async () => {
    if (!supabaseUser?.id) return;
    await fetchUserProfile(supabaseUser.id);
  };

  const updateUserProfile = async (updates: Partial<User & { company?: string; job_title?: string; bio?: string }>) => {
    if (!user) return;

    try {
      const updatedProfile = await ProfileService.updateProfile(user.id, updates);
      
      // Update the user state with the new profile data
      setUser(prev => prev ? { 
        ...prev, 
        ...updatedProfile,
        // Preserve calculated fields that aren't stored in database
        credits: prev.credits,
        role: prev.role
      } : null);
    } catch (error) {
      console.error('❌ Error updating user profile:', error);
      throw error;
    }
  };

  const hasTutorialCompleted = () => {
    return !!(user?.tutorial_completed);
  };

  const markTutorialCompleted = async () => {
    await updateUserProfile({ tutorial_completed: true });
  };

  const hasOnboardingCompleted = () => {
    return !!(user?.onboarding_completed);
  };

  const markOnboardingCompleted = async () => {
    await updateUserProfile({ onboarding_completed: true });
  };

  return (
    <UserContext.Provider value={{
      user,
      isLoadingProfile,
      hasInitialLoad,
      refreshUserProfile,
      updateUserProfile,
      hasTutorialCompleted,
      markTutorialCompleted,
      hasOnboardingCompleted,
      markOnboardingCompleted
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}